/*!
 * Copyright (C) Microsoft Corporation. All rights reserved.
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import * as React from 'react';
import { CompletionItemKind, CompletionTriggerKind, DiagnosticSeverity, } from 'vscode-languageserver-protocol';
import { IconButton } from '@fluentui/react/lib/Button';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { FormulaEditor } from './FormulaEditor';
import { oneLineHeight, } from './PowerFxFormulaEditor.types';
import { PowerFxLanguageClient, TokenResultType } from './PowerFxLanguageClient';
import { NameKind } from './PowerFxSyntaxTypes';
import { CommandName, PowerFxFormulaEditorError } from './FormulaEditor.types';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { RepairHoverProvider } from './repair/RepairHoverProvider';
import { languageName } from './PowerFxSyntax';
var formulaEditorMinHeight = 30;
var minExpandedEditorHeight = 7 * oneLineHeight;
var getClassNames = classNamesFunction();
var PowerFxFormulaEditorBase = /** @class */ (function (_super) {
    __extends(PowerFxFormulaEditorBase, _super);
    function PowerFxFormulaEditorBase(props) {
        var _a, _b;
        var _this = _super.call(this, props) || this;
        _this._editor = null;
        _this._model = null;
        _this._monaco = null;
        _this._languageClient = null;
        _this._listenerDisposable = null;
        _this._version = 0;
        _this._normalizedCompletionLookup = {};
        _this._onNamesChanged = function () { return null; };
        _this._commandExecuted = function (command, args) {
            var _a;
            var lspConfig = _this.props.lspConfig;
            if (lspConfig === null || lspConfig === void 0 ? void 0 : lspConfig.notifyCommandExecuted) {
                (_a = _this._languageClient) === null || _a === void 0 ? void 0 : _a.notifyCommandExecutedAsync(command, args);
            }
        };
        _this._provideCodeAction = function (model, range, context, token) { return __awaiter(_this, void 0, void 0, function () {
            var lspConfig, actionList, textRange, text, codeActionResult;
            var _a, _b, _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        lspConfig = this.props.lspConfig;
                        actionList = {
                            actions: [],
                            dispose: function () {
                                return;
                            },
                        };
                        if (!(lspConfig === null || lspConfig === void 0 ? void 0 : lspConfig.enableCodeAction) || context.markers.length === 0 || !context.only) {
                            return [2 /*return*/, actionList];
                        }
                        textRange = model.getFullModelRange();
                        text = model.getValue();
                        return [4 /*yield*/, ((_a = this._languageClient) === null || _a === void 0 ? void 0 : _a.requestCodeActionAsync(text, {
                                start: { line: textRange.startLineNumber, character: textRange.startColumn },
                                end: { line: textRange.endLineNumber, character: textRange.endColumn },
                            }, context.only))];
                    case 1:
                        codeActionResult = _d.sent();
                        return [2 /*return*/, __assign(__assign({}, actionList), { actions: (_c = (_b = codeActionResult === null || codeActionResult === void 0 ? void 0 : codeActionResult.actions) === null || _b === void 0 ? void 0 : _b.map(function (action) {
                                    var _a;
                                    return ({
                                        kind: action.kind,
                                        title: action.title,
                                        command: {
                                            id: CommandName.CodeActionApplied,
                                            tooltip: 'Quick Fix',
                                            title: 'Code Action',
                                            arguments: [__assign(__assign({}, action), { id: codeActionResult.id })],
                                        },
                                        diagnostics: context.markers,
                                        edit: {
                                            edits: ((_a = Object.values(action.edit.changes[codeActionResult.uri])) === null || _a === void 0 ? void 0 : _a.map(function (value) { return ({
                                                resource: model.uri,
                                                edit: {
                                                    range: new monaco.Range(value.range.start.line, value.range.start.character, value.range.end.line, value.range.end.character),
                                                    text: value.newText,
                                                },
                                            }); })) || [],
                                        },
                                    });
                                })) !== null && _c !== void 0 ? _c : [] })];
                }
            });
        }); };
        _this.updateHeight = function (overrideExpand) {
            var _a, _b;
            var expanded = overrideExpand !== null && overrideExpand !== void 0 ? overrideExpand : _this.state.expanded;
            var prevHeight = _this.state.height;
            var calculatedHeight = _this._getHeight();
            var height = !_this.props.showExpandEditorButton
                ? calculatedHeight
                : expanded
                    ? // when expanded, set the height to full height of the text (upto maxLineCount) but no shorter than 7 lines tall
                        Math.max(minExpandedEditorHeight, calculatedHeight)
                    : // when collapsed, set the height to minLineCount tall but no shorter than formulaEditorMinHeight
                        Math.max(formulaEditorMinHeight, _this.props.minLineCount * oneLineHeight);
            if (height !== prevHeight) {
                _this.setState({ height: height });
                (_b = (_a = _this.props).onHeightUpdated) === null || _b === void 0 ? void 0 : _b.call(_a, height);
            }
        };
        _this._editorDidMount = function (monacoEditor, monacoParam) {
            var _a;
            _this._editor = monacoEditor;
            _this._model = monacoEditor.getModel();
            _this._monaco = monacoParam;
            if (!_this._model || !_this._monaco || _this._model.isDisposed()) {
                return;
            }
            // Create PowerFx language client
            var _b = _this.props, messageProcessor = _b.messageProcessor, getDocumentUriAsync = _b.getDocumentUriAsync, lspConfig = _b.lspConfig, onEditorDidMount = _b.onEditorDidMount;
            _this._languageClient = new PowerFxLanguageClient(getDocumentUriAsync, messageProcessor.sendAsync, _this._handleDiagnosticsNotification, _this._handleTokensNotification);
            if (!(lspConfig === null || lspConfig === void 0 ? void 0 : lspConfig.disableDidOpenNotification)) {
                _this._languageClient.notifyDidOpenAsync(_this._model.getValue());
            }
            if (_this.props.useQuickFixHover) {
                var hoverProvider = new RepairHoverProvider(monacoParam, _this._languageClient, _this._editor, {
                    notifyFixApplied: (_a = _this.props.lspConfig) === null || _a === void 0 ? void 0 : _a.notifyCommandExecuted,
                });
                // Register Hover-based repair suggestions.
                monacoParam.languages.registerHoverProvider(languageName, hoverProvider);
            }
            // Update height
            _this.updateHeight();
            // Notify caller on editor did mount event
            if (onEditorDidMount) {
                onEditorDidMount(monacoEditor, monacoParam);
            }
        };
        _this._handleDiagnosticsNotification = function (params) {
            if (!_this._monaco || !_this._model) {
                return;
            }
            var markers = params.diagnostics.map(function (error) {
                var _a = error.range, start = _a.start, end = _a.end;
                return {
                    severity: _this._getMarkerSeverity(error.severity),
                    message: error.message,
                    startLineNumber: start.line,
                    startColumn: start.character,
                    endLineNumber: end.line,
                    endColumn: end.character + 1,
                };
            });
            _this._monaco.editor.setModelMarkers(_this._model, '', markers);
        };
        _this._provideCompletionAsync = function (currentText, position, currentWordPosition, triggerKind, triggerCharacter) { return __awaiter(_this, void 0, void 0, function () {
            var lspConfig, result, range, precedingCharacter, suggestions;
            var _this = this;
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        lspConfig = this.props.lspConfig;
                        if (lspConfig === null || lspConfig === void 0 ? void 0 : lspConfig.disableCompletionRequest) {
                            return [2 /*return*/, { suggestions: [] }];
                        }
                        return [4 /*yield*/, ((_a = this._languageClient) === null || _a === void 0 ? void 0 : _a.requestProvideCompletionItemsAsync(currentText, position.lineNumber - 1, position.column - 1, this._getCompletionTriggerKind(triggerKind), triggerCharacter))];
                    case 1:
                        result = _c.sent();
                        if (!result) {
                            return [2 /*return*/, { suggestions: [] }];
                        }
                        range = currentWordPosition
                            ? {
                                startColumn: currentWordPosition.startColumn,
                                endColumn: currentWordPosition.endColumn,
                                startLineNumber: position.lineNumber,
                                endLineNumber: position.lineNumber,
                            }
                            : {
                                startColumn: position.column,
                                endColumn: position.column,
                                startLineNumber: position.lineNumber,
                                endLineNumber: position.lineNumber,
                            };
                        precedingCharacter = (_b = this._model) === null || _b === void 0 ? void 0 : _b.getValueInRange({
                            startColumn: range.startColumn - 1,
                            endColumn: range.startColumn,
                            startLineNumber: range.startLineNumber,
                            endLineNumber: range.endLineNumber,
                        });
                        suggestions = result.items.map(function (item) {
                            var label = item.label;
                            var insertText = label.charAt(0) === "'" && precedingCharacter === "'" ? label.substring(1, label.length) : label;
                            _this._normalizedCompletionLookup[label.toLowerCase()] = label;
                            return {
                                label: label,
                                documentation: item.documentation,
                                detail: item.detail,
                                kind: _this._getCompletionKind(item.kind),
                                range: range,
                                insertText: insertText,
                            };
                        });
                        return [2 /*return*/, {
                                incomplete: !currentWordPosition,
                                suggestions: suggestions,
                            }];
                }
            });
        }); };
        _this._handleTokensNotification = function (params) {
            var e_1, _a;
            var _b;
            if (!_this._monaco || !_this._model) {
                return;
            }
            var names = [];
            try {
                for (var _c = __values(Object.entries(params.tokens)), _d = _c.next(); !_d.done; _d = _c.next()) {
                    var _e = __read(_d.value, 2), key = _e[0], value = _e[1];
                    _this._normalizedCompletionLookup[key.toLowerCase()] = key;
                    switch (value) {
                        case TokenResultType.Function:
                            names.push({ name: key, kind: NameKind.Function });
                            break;
                        case TokenResultType.Variable:
                            names.push({ name: key, kind: NameKind.Variable });
                            break;
                        case TokenResultType.HostSymbol:
                            names.push({ name: key, kind: NameKind.HostSymbol });
                            break;
                        default:
                            break;
                    }
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
                }
                finally { if (e_1) throw e_1.error; }
            }
            (_b = _this._onNamesChanged) === null || _b === void 0 ? void 0 : _b.call(_this, names);
        };
        _this._provideSignatureHelpAsync = function (currentText, position, context) { return __awaiter(_this, void 0, void 0, function () {
            var noResult, lspConfig, result;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        noResult = {
                            value: {
                                signatures: [],
                                activeSignature: 0,
                                activeParameter: 0,
                            },
                            dispose: function () {
                                return;
                            },
                        };
                        lspConfig = this.props.lspConfig;
                        if (!(lspConfig === null || lspConfig === void 0 ? void 0 : lspConfig.enableSignatureHelpRequest)) {
                            return [2 /*return*/, noResult];
                        }
                        return [4 /*yield*/, ((_a = this._languageClient) === null || _a === void 0 ? void 0 : _a.requestProvideSignatureHelpAsync(currentText, position.lineNumber - 1, position.column - 1))];
                    case 1:
                        result = _b.sent();
                        if (!result || result.signatures.length === 0) {
                            return [2 /*return*/, noResult];
                        }
                        return [2 /*return*/, {
                                value: {
                                    signatures: result.signatures.map(function (item) { return ({
                                        label: item.label,
                                        documentation: item.documentation,
                                        parameters: item.parameters || [],
                                        activeParameter: item.activeParameter,
                                    }); }),
                                    activeSignature: result.activeSignature || 0,
                                    activeParameter: result.activeParameter || 0,
                                },
                                dispose: function () {
                                    return;
                                },
                            }];
                }
            });
        }); };
        _this._onContentChanged = function (value, editorContentChangeEvent) { return __awaiter(_this, void 0, void 0, function () {
            var _a, onChange, lspConfig;
            var _b;
            return __generator(this, function (_c) {
                _a = this.props, onChange = _a.onChange, lspConfig = _a.lspConfig;
                if (onChange) {
                    onChange(value, editorContentChangeEvent);
                }
                if (!(lspConfig === null || lspConfig === void 0 ? void 0 : lspConfig.disableDidChangeNotification)) {
                    (_b = this._languageClient) === null || _b === void 0 ? void 0 : _b.notifyDidChangeAsync(value, this._version++);
                }
                // Update height
                this.updateHeight();
                return [2 /*return*/];
            });
        }); };
        _this._getCompletionTriggerKind = function (kind) {
            switch (kind) {
                case monaco.languages.CompletionTriggerKind.Invoke:
                    return CompletionTriggerKind.Invoked;
                case monaco.languages.CompletionTriggerKind.TriggerCharacter:
                    return CompletionTriggerKind.TriggerCharacter;
                case monaco.languages.CompletionTriggerKind.TriggerForIncompleteCompletions:
                    return CompletionTriggerKind.TriggerForIncompleteCompletions;
                default:
                    throw new Error('Unknown trigger kind!');
            }
        };
        _this._getCompletionKind = function (kind) {
            switch (kind) {
                case CompletionItemKind.Text:
                    return monaco.languages.CompletionItemKind.Text;
                case CompletionItemKind.Method:
                    return monaco.languages.CompletionItemKind.Method;
                case CompletionItemKind.Function:
                    return monaco.languages.CompletionItemKind.Function;
                case CompletionItemKind.Constructor:
                    return monaco.languages.CompletionItemKind.Constructor;
                case CompletionItemKind.Field:
                    return monaco.languages.CompletionItemKind.Field;
                case CompletionItemKind.Variable:
                    return monaco.languages.CompletionItemKind.Variable;
                case CompletionItemKind.Class:
                    return monaco.languages.CompletionItemKind.Class;
                case CompletionItemKind.Interface:
                    return monaco.languages.CompletionItemKind.Interface;
                case CompletionItemKind.Module:
                    return monaco.languages.CompletionItemKind.Module;
                case CompletionItemKind.Property:
                    return monaco.languages.CompletionItemKind.Property;
                case CompletionItemKind.Unit:
                    return monaco.languages.CompletionItemKind.Unit;
                case CompletionItemKind.Value:
                    return monaco.languages.CompletionItemKind.Value;
                case CompletionItemKind.Enum:
                    return monaco.languages.CompletionItemKind.Enum;
                case CompletionItemKind.Keyword:
                    return monaco.languages.CompletionItemKind.Keyword;
                case CompletionItemKind.Snippet:
                    return monaco.languages.CompletionItemKind.Snippet;
                case CompletionItemKind.Color:
                    return monaco.languages.CompletionItemKind.Color;
                case CompletionItemKind.File:
                    return monaco.languages.CompletionItemKind.File;
                case CompletionItemKind.Reference:
                    return monaco.languages.CompletionItemKind.Reference;
                case CompletionItemKind.Folder:
                    return monaco.languages.CompletionItemKind.Folder;
                case CompletionItemKind.EnumMember:
                    return monaco.languages.CompletionItemKind.EnumMember;
                case CompletionItemKind.Constant:
                    return monaco.languages.CompletionItemKind.Constant;
                case CompletionItemKind.Struct:
                    return monaco.languages.CompletionItemKind.Struct;
                case CompletionItemKind.Event:
                    return monaco.languages.CompletionItemKind.Event;
                case CompletionItemKind.Operator:
                    return monaco.languages.CompletionItemKind.Operator;
                case CompletionItemKind.TypeParameter:
                    return monaco.languages.CompletionItemKind.TypeParameter;
                default:
                    return monaco.languages.CompletionItemKind.Method;
            }
        };
        _this._getMarkerSeverity = function (severity) {
            switch (severity) {
                case DiagnosticSeverity.Error:
                    return monaco.MarkerSeverity.Error;
                case DiagnosticSeverity.Hint:
                    return monaco.MarkerSeverity.Hint;
                case DiagnosticSeverity.Information:
                    return monaco.MarkerSeverity.Info;
                case DiagnosticSeverity.Warning:
                    return monaco.MarkerSeverity.Warning;
                default:
                    return monaco.MarkerSeverity.Error;
            }
        };
        var appInsightsConnectionString = (_a = _this.props.telemetryConfig) === null || _a === void 0 ? void 0 : _a.appInsightsConnectionString;
        if (((_b = _this.props.telemetryConfig) === null || _b === void 0 ? void 0 : _b.enableTelemetry) !== false && appInsightsConnectionString) {
            _this.initTelemetry(appInsightsConnectionString);
        }
        _this.state = {
            expanded: false,
            height: _this._getHeight(),
        };
        _this._listenerDisposable = _this.props.messageProcessor.addListener(function (payload) {
            var _a;
            (_a = _this._languageClient) === null || _a === void 0 ? void 0 : _a.onDataReceivedFromLanguageServer(payload);
        });
        return _this;
    }
    PowerFxFormulaEditorBase.prototype.initTelemetry = function (appInsightsConnectionString) {
        var _this = this;
        var _a, _b, _c, _d, _e;
        var appInsights = new ApplicationInsights({
            config: {
                connectionString: appInsightsConnectionString,
                disableAjaxTracking: true,
                disableFetchTracking: true,
            },
        });
        appInsights.config.disableCookiesUsage = (_a = this.props.telemetryConfig) === null || _a === void 0 ? void 0 : _a.disableCookies;
        var productName = ((_b = this.props.telemetryConfig) === null || _b === void 0 ? void 0 : _b.product) ? (_c = this.props.telemetryConfig) === null || _c === void 0 ? void 0 : _c.product : 'Default';
        var telemetryInitializer = function (envelope) {
            var _a, _b;
            if (envelope.baseData) {
                envelope.baseData.name = productName;
            }
            if ((_a = _this.props.telemetryConfig) === null || _a === void 0 ? void 0 : _a.productArea) {
                if (envelope.data) {
                    envelope.data.productArea = (_b = _this.props.telemetryConfig) === null || _b === void 0 ? void 0 : _b.productArea;
                }
            }
        };
        appInsights.addTelemetryInitializer(telemetryInitializer);
        try {
            appInsights.loadAppInsights();
            appInsights.trackPageView();
        }
        catch (error) {
            (_e = (_d = this.props) === null || _d === void 0 ? void 0 : _d.onError) === null || _e === void 0 ? void 0 : _e.call(_d, error);
        }
    };
    PowerFxFormulaEditorBase.prototype.componentWillUnmount = function () {
        var _a;
        (_a = this._listenerDisposable) === null || _a === void 0 ? void 0 : _a.dispose();
        this._listenerDisposable = null;
    };
    PowerFxFormulaEditorBase.prototype.componentDidUpdate = function (prevProps) {
        // Update the height of the Monaco editor when min line count changes. This allows the min line count to be
        // adjusted by the consumer and to have those changes immediately reflected in the height of the editor.
        if (this.props.minLineCount !== prevProps.minLineCount) {
            this.updateHeight();
        }
    };
    PowerFxFormulaEditorBase.prototype.render = function () {
        var _this = this;
        var _a;
        var _b = this.props, styles = _b.styles, theme = _b.theme, defaultValue = _b.defaultValue, onBlur = _b.onBlur, onKeyDown = _b.onKeyDown, onKeyUp = _b.onKeyUp, onFocus = _b.onFocus, monacoEditorOptions = _b.monacoEditorOptions, showExpandEditorButton = _b.showExpandEditorButton, expandEditorButtonAriaLabel = _b.expandEditorButtonAriaLabel, editorFocusOnMount = _b.editorFocusOnMount, onDidChangeCursorPosition = _b.onDidChangeCursorPosition, onDidChangeCursorSelection = _b.onDidChangeCursorSelection;
        var _c = this.state, height = _c.height, expanded = _c.expanded;
        var classNames = getClassNames(styles, {
            theme: theme,
            showExpandEditorButton: !!showExpandEditorButton,
        });
        var expandEditorIconStyles = (_a = classNames.subComponentStyles) === null || _a === void 0 ? void 0 : _a.expandEditorIcon({});
        return (React.createElement("div", { id: "root", className: classNames.root },
            React.createElement("div", { id: "formulaEditor", className: classNames.formulaEditor },
                React.createElement(FormulaEditor, { locked: this.props.locked, defaultValue: defaultValue, monacoEditorOptions: monacoEditorOptions, height: height, 
                    // Might be nice for this be true, but it interferes with the selection widget.
                    leaveOnEnterKey: false, onEditorDidMount: this._editorDidMount, syntaxParameters: {
                        highlightedNames: [],
                        useSemicolons: false,
                        subscribeToNames: function (namesChanged) {
                            _this._onNamesChanged = namesChanged;
                        },
                        getNormalizedCompletionLookup: function () { return _this._normalizedCompletionLookup; },
                    }, focusOnMount: editorFocusOnMount, onChange: this._onContentChanged, onBlur: onBlur, onKeyDown: onKeyDown, onKeyUp: onKeyUp, onFocus: onFocus, completionProvider: function (model, position, context) {
                        // Note: we call model methods up-front to avoid disposed model issue
                        // since there are other async operations inside _provideCompletionAsync
                        return _this._provideCompletionAsync(model.getValue(), position, model.getWordAtPosition(position), context.triggerKind, context.triggerCharacter);
                    }, signatureHelpProvider: function (model, position, token, context) { return _this._provideSignatureHelpAsync(model.getValue(), position, context); }, codeActionProvider: this._provideCodeAction, onDidChangeCursorPosition: onDidChangeCursorPosition, onDidChangeCursorSelection: onDidChangeCursorSelection, onCommandExecuted: this._commandExecuted })),
            showExpandEditorButton && (React.createElement(IconButton, { styles: expandEditorIconStyles, iconProps: { iconName: expanded ? 'ChevronUp' : 'ChevronDown' }, onClick: function () {
                    _this.setState({ expanded: !expanded });
                    _this.updateHeight(!expanded);
                }, ariaLabel: expandEditorButtonAriaLabel }))));
    };
    PowerFxFormulaEditorBase.prototype._getHeight = function () {
        var _a, _b;
        var _c = this.props, minLineCount = _c.minLineCount, maxLineCount = _c.maxLineCount;
        if (minLineCount === maxLineCount) {
            return Math.max(formulaEditorMinHeight, maxLineCount * oneLineHeight);
        }
        var height = oneLineHeight;
        // TODO: A better solution is to use editor.onDidContentSizeChange and editor.getContentHeight() added in monaco-editor 0.20.0 to
        // automatically set the height
        if (this._editor && this._model && !this._model.isDisposed()) {
            var lineCount = this._model.getLineCount() || 1;
            try {
                if (lineCount <= 1 && height > 200) {
                    // recent version of monaco editor 0.27.0 returns inconsistent
                    // height such as getTopForLineNumber(1) === 0 and getTopForLineNumber(2) === 703
                    // when the model has no content or a single line of content without newline.
                    // allow this behavior to be trackable
                    throw new PowerFxFormulaEditorError("fx editor computed invalid height " + height + " for lineCount " + lineCount);
                }
            }
            catch (error) {
                (_b = (_a = this.props) === null || _a === void 0 ? void 0 : _a.onError) === null || _b === void 0 ? void 0 : _b.call(_a, error);
            }
            height = this._editor.getTopForLineNumber(lineCount + 1) + oneLineHeight;
        }
        return Math.min(Math.max(formulaEditorMinHeight, maxLineCount * oneLineHeight), Math.max(formulaEditorMinHeight, minLineCount * oneLineHeight, height));
    };
    PowerFxFormulaEditorBase.defaultProps = {
        editorFocusOnMount: true,
        /**  Enables telemtery by default, if appInsightsConnectionString was provided. */
        telemetryConfig: { enableTelemetry: true, disableCookies: false },
    };
    return PowerFxFormulaEditorBase;
}(React.Component));
export { PowerFxFormulaEditorBase };
//# sourceMappingURL=PowerFxFormulaEditor.base.js.map